import * as z from 'zod';

export const GetDisputeRequest = z.object({
  disputeId: z
    .string()
    .describe('ID of the dispute to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetDisputeResponse = z.object({
  _id: z
    .string()
    .describe('Dispute ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  chargeId: z
    .string()
    .describe('ID of the charge that is being disputed.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  providerDisputeId: z
    .string()
    .describe(
      'Unique identifier assigned by the payment service provider for this dispute.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  stage: z
    .enum(['CHARGEBACK', 'INQUIRY'])
    .describe('Stage of the dispute process.')
    .optional(),
  reason: z
    .enum([
      'FRAUD_CARD_PRESENT',
      'FRAUD_CARD_ABSENT',
      'DUPLICATE_PROCESSING',
      'SERVICES_NOT_PROVIDED',
      'CANCELED_RECURRING',
      'NOT_AS_DESCRIBED',
      'COUNTERFEIT',
      'MISREPRESENTATION',
      'CANCELED',
      'OTHER',
    ])
    .describe('Reason why the customer initiated the dispute.')
    .optional(),
  currencyCode: z
    .string()
    .describe(
      'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. Matches the currency of the disputed charge.'
    )
    .optional()
    .nullable(),
  amount: z
    .string()
    .describe(
      'Disputed amount in the currency\'s main units. For example, `"12.95"` for $12.95.'
    )
    .optional()
    .nullable(),
  status: z
    .enum(['WAITING_MERCHANT', 'UNDER_REVIEW', 'WAITING_BUYER', 'WON', 'LOST'])
    .describe(
      'Current status of the dispute. This indicates what action is required and who needs to take it.'
    )
    .optional(),
  sellerProtection: z
    .enum(['NOT_ELIGIBLE', 'ELIGIBLE', 'EXTENDED'])
    .describe(
      'Level of seller protection coverage for this dispute.\n\nSeller protection is set by the payment provider. For example, PayPal offers a [seller protection program](https://www.paypal.com/ua/legalhub/paypal/seller-protection).'
    )
    .optional(),
  channel: z
    .enum(['INTERNAL', 'EXTERNAL'])
    .describe('Channel through which the dispute is being processed.')
    .optional(),
  dueDate: z
    .date()
    .describe(
      'The latest date and time until which the dispute can remain in its current status.\n\nIf this date passes, the dispute will be resolved against the party that is required to take action. For example, if the status is `WAITING_MERCHANT`, and the due date passes, the dispute will be `LOST` and a refund will be processed automatically.'
    )
    .optional()
    .nullable(),
  networkReasonCode: z
    .string()
    .describe(
      'Network reason code provided by the card network or issuing bank that initiated the dispute.'
    )
    .min(1)
    .max(64)
    .optional()
    .nullable(),
  defendable: z
    .boolean()
    .describe('Whether the dispute can be defended with evidence.')
    .optional()
    .nullable(),
  autoDefended: z
    .boolean()
    .describe(
      'Whether the dispute was automatically defended by the payment processor.'
    )
    .optional()
    .nullable(),
  defendedDate: z
    .date()
    .describe('Date and time at which the dispute was defended.')
    .optional()
    .nullable(),
  acceptedDate: z
    .date()
    .describe('Date and time at which the dispute was accepted.')
    .optional()
    .nullable(),
  actions: z
    .array(
      z.object({
        type: z
          .enum(['ACCEPT', 'DEFEND'])
          .describe('Type of action that can be performed.')
          .optional(),
        dueDate: z
          .date()
          .describe(
            'Latest date and time by which this action must be performed.'
          )
          .optional()
          .nullable(),
      })
    )
    .max(20)
    .optional(),
  _createdDate: z
    .date()
    .describe('Date and time the dispute was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the dispute was updated.')
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the dispute is updated. To prevent conflicting changes, the current revision must be passed when updating the dispute. Ignored when creating a dispute.'
    )
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Custom field data for the dispute object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
    )
    .optional(),
  tags: z
    .object({
      privateTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
        )
        .optional(),
      publicTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
        )
        .optional(),
    })
    .describe(
      'Tags assigned to the dispute for organization and filtering purposes.'
    )
    .optional(),
});
export const QueryDisputesRequest = z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object in the following format:\n`"filter" : {\n"fieldName1": "value1",\n"fieldName2":{"$operator":"value2"}\n}`\nExample of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(5)
          .optional(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.'
            ),
        }),
      ])
    )
    .describe('WQL expression for filtering and sorting disputes.'),
});
export const QueryDisputesResponse = z.object({
  disputes: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Dispute ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        chargeId: z
          .string()
          .describe('ID of the charge that is being disputed.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        providerDisputeId: z
          .string()
          .describe(
            'Unique identifier assigned by the payment service provider for this dispute.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        stage: z
          .enum(['CHARGEBACK', 'INQUIRY'])
          .describe('Stage of the dispute process.')
          .optional(),
        reason: z
          .enum([
            'FRAUD_CARD_PRESENT',
            'FRAUD_CARD_ABSENT',
            'DUPLICATE_PROCESSING',
            'SERVICES_NOT_PROVIDED',
            'CANCELED_RECURRING',
            'NOT_AS_DESCRIBED',
            'COUNTERFEIT',
            'MISREPRESENTATION',
            'CANCELED',
            'OTHER',
          ])
          .describe('Reason why the customer initiated the dispute.')
          .optional(),
        currencyCode: z
          .string()
          .describe(
            'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. Matches the currency of the disputed charge.'
          )
          .optional()
          .nullable(),
        amount: z
          .string()
          .describe(
            'Disputed amount in the currency\'s main units. For example, `"12.95"` for $12.95.'
          )
          .optional()
          .nullable(),
        status: z
          .enum([
            'WAITING_MERCHANT',
            'UNDER_REVIEW',
            'WAITING_BUYER',
            'WON',
            'LOST',
          ])
          .describe(
            'Current status of the dispute. This indicates what action is required and who needs to take it.'
          )
          .optional(),
        sellerProtection: z
          .enum(['NOT_ELIGIBLE', 'ELIGIBLE', 'EXTENDED'])
          .describe(
            'Level of seller protection coverage for this dispute.\n\nSeller protection is set by the payment provider. For example, PayPal offers a [seller protection program](https://www.paypal.com/ua/legalhub/paypal/seller-protection).'
          )
          .optional(),
        channel: z
          .enum(['INTERNAL', 'EXTERNAL'])
          .describe('Channel through which the dispute is being processed.')
          .optional(),
        dueDate: z
          .date()
          .describe(
            'The latest date and time until which the dispute can remain in its current status.\n\nIf this date passes, the dispute will be resolved against the party that is required to take action. For example, if the status is `WAITING_MERCHANT`, and the due date passes, the dispute will be `LOST` and a refund will be processed automatically.'
          )
          .optional()
          .nullable(),
        networkReasonCode: z
          .string()
          .describe(
            'Network reason code provided by the card network or issuing bank that initiated the dispute.'
          )
          .min(1)
          .max(64)
          .optional()
          .nullable(),
        defendable: z
          .boolean()
          .describe('Whether the dispute can be defended with evidence.')
          .optional()
          .nullable(),
        autoDefended: z
          .boolean()
          .describe(
            'Whether the dispute was automatically defended by the payment processor.'
          )
          .optional()
          .nullable(),
        defendedDate: z
          .date()
          .describe('Date and time at which the dispute was defended.')
          .optional()
          .nullable(),
        acceptedDate: z
          .date()
          .describe('Date and time at which the dispute was accepted.')
          .optional()
          .nullable(),
        actions: z
          .array(
            z.object({
              type: z
                .enum(['ACCEPT', 'DEFEND'])
                .describe('Type of action that can be performed.')
                .optional(),
              dueDate: z
                .date()
                .describe(
                  'Latest date and time by which this action must be performed.'
                )
                .optional()
                .nullable(),
            })
          )
          .max(20)
          .optional(),
        _createdDate: z
          .date()
          .describe('Date and time the dispute was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the dispute was updated.')
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the dispute is updated. To prevent conflicting changes, the current revision must be passed when updating the dispute. Ignored when creating a dispute.'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for the dispute object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
              )
              .optional(),
            publicTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe(
            'Tags assigned to the dispute for organization and filtering purposes.'
          )
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata for cursor-based pagination.')
    .optional(),
});
export const AcceptDisputeRequest = z.object({
  disputeId: z
    .string()
    .describe('ID of the dispute to accept.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const AcceptDisputeResponse = z.object({
  dispute: z
    .object({
      _id: z
        .string()
        .describe('Dispute ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      chargeId: z
        .string()
        .describe('ID of the charge that is being disputed.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      providerDisputeId: z
        .string()
        .describe(
          'Unique identifier assigned by the payment service provider for this dispute.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      stage: z
        .enum(['CHARGEBACK', 'INQUIRY'])
        .describe('Stage of the dispute process.')
        .optional(),
      reason: z
        .enum([
          'FRAUD_CARD_PRESENT',
          'FRAUD_CARD_ABSENT',
          'DUPLICATE_PROCESSING',
          'SERVICES_NOT_PROVIDED',
          'CANCELED_RECURRING',
          'NOT_AS_DESCRIBED',
          'COUNTERFEIT',
          'MISREPRESENTATION',
          'CANCELED',
          'OTHER',
        ])
        .describe('Reason why the customer initiated the dispute.')
        .optional(),
      currencyCode: z
        .string()
        .describe(
          'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. Matches the currency of the disputed charge.'
        )
        .optional()
        .nullable(),
      amount: z
        .string()
        .describe(
          'Disputed amount in the currency\'s main units. For example, `"12.95"` for $12.95.'
        )
        .optional()
        .nullable(),
      status: z
        .enum([
          'WAITING_MERCHANT',
          'UNDER_REVIEW',
          'WAITING_BUYER',
          'WON',
          'LOST',
        ])
        .describe(
          'Current status of the dispute. This indicates what action is required and who needs to take it.'
        )
        .optional(),
      sellerProtection: z
        .enum(['NOT_ELIGIBLE', 'ELIGIBLE', 'EXTENDED'])
        .describe(
          'Level of seller protection coverage for this dispute.\n\nSeller protection is set by the payment provider. For example, PayPal offers a [seller protection program](https://www.paypal.com/ua/legalhub/paypal/seller-protection).'
        )
        .optional(),
      channel: z
        .enum(['INTERNAL', 'EXTERNAL'])
        .describe('Channel through which the dispute is being processed.')
        .optional(),
      dueDate: z
        .date()
        .describe(
          'The latest date and time until which the dispute can remain in its current status.\n\nIf this date passes, the dispute will be resolved against the party that is required to take action. For example, if the status is `WAITING_MERCHANT`, and the due date passes, the dispute will be `LOST` and a refund will be processed automatically.'
        )
        .optional()
        .nullable(),
      networkReasonCode: z
        .string()
        .describe(
          'Network reason code provided by the card network or issuing bank that initiated the dispute.'
        )
        .min(1)
        .max(64)
        .optional()
        .nullable(),
      defendable: z
        .boolean()
        .describe('Whether the dispute can be defended with evidence.')
        .optional()
        .nullable(),
      autoDefended: z
        .boolean()
        .describe(
          'Whether the dispute was automatically defended by the payment processor.'
        )
        .optional()
        .nullable(),
      defendedDate: z
        .date()
        .describe('Date and time at which the dispute was defended.')
        .optional()
        .nullable(),
      acceptedDate: z
        .date()
        .describe('Date and time at which the dispute was accepted.')
        .optional()
        .nullable(),
      actions: z
        .array(
          z.object({
            type: z
              .enum(['ACCEPT', 'DEFEND'])
              .describe('Type of action that can be performed.')
              .optional(),
            dueDate: z
              .date()
              .describe(
                'Latest date and time by which this action must be performed.'
              )
              .optional()
              .nullable(),
          })
        )
        .max(20)
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the dispute was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the dispute was updated.')
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the dispute is updated. To prevent conflicting changes, the current revision must be passed when updating the dispute. Ignored when creating a dispute.'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the dispute object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags assigned to the dispute for organization and filtering purposes.'
        )
        .optional(),
    })
    .describe('Updated dispute after acceptance.')
    .optional(),
});
export const DefendDisputeRequest = z.object({
  disputeId: z
    .string()
    .describe('ID of the dispute to defend.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DefendDisputeResponse = z.object({
  dispute: z
    .object({
      _id: z
        .string()
        .describe('Dispute ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      chargeId: z
        .string()
        .describe('ID of the charge that is being disputed.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      providerDisputeId: z
        .string()
        .describe(
          'Unique identifier assigned by the payment service provider for this dispute.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      stage: z
        .enum(['CHARGEBACK', 'INQUIRY'])
        .describe('Stage of the dispute process.')
        .optional(),
      reason: z
        .enum([
          'FRAUD_CARD_PRESENT',
          'FRAUD_CARD_ABSENT',
          'DUPLICATE_PROCESSING',
          'SERVICES_NOT_PROVIDED',
          'CANCELED_RECURRING',
          'NOT_AS_DESCRIBED',
          'COUNTERFEIT',
          'MISREPRESENTATION',
          'CANCELED',
          'OTHER',
        ])
        .describe('Reason why the customer initiated the dispute.')
        .optional(),
      currencyCode: z
        .string()
        .describe(
          'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. Matches the currency of the disputed charge.'
        )
        .optional()
        .nullable(),
      amount: z
        .string()
        .describe(
          'Disputed amount in the currency\'s main units. For example, `"12.95"` for $12.95.'
        )
        .optional()
        .nullable(),
      status: z
        .enum([
          'WAITING_MERCHANT',
          'UNDER_REVIEW',
          'WAITING_BUYER',
          'WON',
          'LOST',
        ])
        .describe(
          'Current status of the dispute. This indicates what action is required and who needs to take it.'
        )
        .optional(),
      sellerProtection: z
        .enum(['NOT_ELIGIBLE', 'ELIGIBLE', 'EXTENDED'])
        .describe(
          'Level of seller protection coverage for this dispute.\n\nSeller protection is set by the payment provider. For example, PayPal offers a [seller protection program](https://www.paypal.com/ua/legalhub/paypal/seller-protection).'
        )
        .optional(),
      channel: z
        .enum(['INTERNAL', 'EXTERNAL'])
        .describe('Channel through which the dispute is being processed.')
        .optional(),
      dueDate: z
        .date()
        .describe(
          'The latest date and time until which the dispute can remain in its current status.\n\nIf this date passes, the dispute will be resolved against the party that is required to take action. For example, if the status is `WAITING_MERCHANT`, and the due date passes, the dispute will be `LOST` and a refund will be processed automatically.'
        )
        .optional()
        .nullable(),
      networkReasonCode: z
        .string()
        .describe(
          'Network reason code provided by the card network or issuing bank that initiated the dispute.'
        )
        .min(1)
        .max(64)
        .optional()
        .nullable(),
      defendable: z
        .boolean()
        .describe('Whether the dispute can be defended with evidence.')
        .optional()
        .nullable(),
      autoDefended: z
        .boolean()
        .describe(
          'Whether the dispute was automatically defended by the payment processor.'
        )
        .optional()
        .nullable(),
      defendedDate: z
        .date()
        .describe('Date and time at which the dispute was defended.')
        .optional()
        .nullable(),
      acceptedDate: z
        .date()
        .describe('Date and time at which the dispute was accepted.')
        .optional()
        .nullable(),
      actions: z
        .array(
          z.object({
            type: z
              .enum(['ACCEPT', 'DEFEND'])
              .describe('Type of action that can be performed.')
              .optional(),
            dueDate: z
              .date()
              .describe(
                'Latest date and time by which this action must be performed.'
              )
              .optional()
              .nullable(),
          })
        )
        .max(20)
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the dispute was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the dispute was updated.')
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the dispute is updated. To prevent conflicting changes, the current revision must be passed when updating the dispute. Ignored when creating a dispute.'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the dispute object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags assigned to the dispute for organization and filtering purposes.'
        )
        .optional(),
    })
    .describe('Updated dispute after submitting evidence.')
    .optional(),
});
export const BulkUpdateDisputeTagsRequest = z.object({
  ids: z.array(z.string()).min(1).max(100),
  options: z
    .object({
      assignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to assign to the disputes.')
        .optional(),
      unassignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to remove from the disputes.')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateDisputeTagsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for the individual dispute update.')
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata about the bulk operation.')
    .optional(),
});
export const BulkUpdateDisputeTagsByFilterRequest = z.object({
  filter: z
    .record(z.string(), z.any())
    .describe(
      'Filter that determines which disputes to update tags for.\n\nAn empty filter updates all disputes.'
    ),
  options: z
    .object({
      assignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to assign to the filtered disputes.')
        .optional(),
      unassignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to remove from the filtered disputes.')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateDisputeTagsByFilterResponse = z.object({
  jobId: z
    .string()
    .describe('Job ID for tracking the asynchronous bulk update operation.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
